import React from 'react'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { theme } from '../../../../theme/theme'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { CreateUpdateRegisterUnitItemForm } from '../CreateUpdateRegisterUnitItemForm'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { unitCreateSchema } from '../../../../forms/schemas/society_unit_create'
import { NUnits } from '../../../../interfaces/services/units.interfaces'
import { IUnitModel } from '../../../../interfaces/models/units.interfaces'
import { createUnit, patchUnit } from '../../../../api/units'
import { i18nSocietyType } from '../../../../i18n'

interface CreateUpdateRegisterUnitItemModalProps {
  show: boolean
  close: () => void
  unit?: IUnitModel
  children?: React.ReactNode
}

export const CreateUpdateRegisterUnitItemModal = ({
  show,
  close,
  unit,
  children,
}: CreateUpdateRegisterUnitItemModalProps): JSX.Element => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!unit

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate(
        updateMode
          ? 'registerUnit.flashMessage.updateUnitSuccess'
          : 'registerUnit.flashMessage.createUnitSuccess',
        { context: i18nSocietyType(society?.societyTypeDisplayName) }
      )
    )
  }

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate(
        updateMode
          ? 'registerUnit.flashMessage.updateUnitFailure'
          : 'registerUnit.flashMessage.createUnitFailure',
        { context: i18nSocietyType(society?.societyTypeDisplayName) }
      )
    )
  }

  const { mutate: mutatePatchUnit, isPending: isPatchingUnit } = useMutation({
    mutationFn: patchUnit,
    onSuccess: () => {
      close()
      showSuccessMessage()
    },
    onError: () => {
      showErrorMessage()
    },
  })

  const { mutate: mutateCreatUnit, isPending: isCreatingUnit } = useMutation({
    mutationFn: createUnit,
    onSuccess: () => {
      close()
      showSuccessMessage()
    },
    onError: () => {
      showErrorMessage()
    },
  })

  const onSubmit = async (
    data: InferType<typeof unitCreateSchema>
  ): Promise<void> => {
    if (updateMode) {
      mutatePatchUnit({ id: unit._id, body: data })
    } else {
      mutateCreatUnit(data as NUnits.NCreate.IRequestBody)
    }
  }

  const onError = (): void => {
    if (updateMode) {
      setToastNotification(
        ToastType.DANGER,
        translate('registerUnit.flashMessage.updateUnitFailure', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })
      )
    } else {
      setToastNotification(
        ToastType.DANGER,
        translate('registerUnit.flashMessage.createUnitFailure', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })
      )
    }
  }

  const getModalTitle = (): string => {
    if (updateMode) {
      return translate('registerUnit.modal.update', {
        context: i18nSocietyType(society?.societyTypeDisplayName),
      })
    }
    return translate('registerUnit.modal.create', {
      context: i18nSocietyType(society?.societyTypeDisplayName),
    })
  }

  const loading = isCreatingUnit || isPatchingUnit
  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              <div>
                <p style={theme.textVariants.h3}>{getModalTitle()}</p>
              </div>
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              {society && (
                <CreateUpdateRegisterUnitItemForm
                  onError={onError}
                  onSubmit={onSubmit}
                  onClose={close}
                  loading={loading}
                  unit={unit}
                  society={society}
                />
              )}
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
